import { AiCreditUsageTarget, SettingKeyType } from '@crew/enums/app'
import {
  AiCreditUsageTarget as AiCreditUsageTargetDomain,
  ProjectType,
} from '@crew/enums/domain'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'
import {
  ColumnDef,
  getCoreRowModel,
  OnChangeFn,
  PaginationState,
  SortingState,
  TableOptions,
} from '@tanstack/react-table'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import {
  AI_CREDIT_USAGE_HISTORY_DEFAULT_SORT_COLUMN,
  DEFAULT_PAGING_PARAMS,
} from 'configs/constants'
import qs from 'qs'
import { FC, memo, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getParamAsArray, getParamAsString } from 'utils'
import _ from 'lodash'
import { getDefaultTabValue, getEnumDataByValue } from '@crew/utils/enum'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { useGetAiCreditUsageHistoriesQuery } from '@crew/apis/aiCreditUsageHistory/aiCreditUsageHistoryApis'
import { History } from '@crew/apis/aiCreditUsageHistory/models/getAiCreditUsageHistories/response'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { EntityType } from '@crew/enums/domain'
import { EventDetailTabs, TaskDetailListTabs } from 'enums/app'
import { JsonDateFormat } from '@crew/enums/system'
import dayjs from '@crew/modules'
import { CrewEntityIcon } from 'components/elements/crewEntityIcon/crewEntityIcon'

type AiCreditUsageHistoryTableColumns = History

// AIクレジット利用履歴テーブルコンポーネント
export const AiCreditUsageHistoryTable: FC = memo(() => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const params = qs.parse(searchParams.toString())

  // ユーザーのデフォルトの表示件数
  const defaultListDisplayNumber = useUserSetting(
    SettingKeyType.ListDisplayNumber,
    DEFAULT_PAGING_PARAMS.pageSize
  )

  // ページネーションの状態。クエリパラメータから初期値を取得
  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: Number(
        getParamAsString('pageIndex', params) ?? DEFAULT_PAGING_PARAMS.pageIndex
      ),
      pageSize: Number(
        getParamAsString('pageSize', params) ?? defaultListDisplayNumber
      ),
    }),
    [defaultListDisplayNumber, params]
  )

  // ソート順。指定されていない場合はIDの降順
  const sortParams = useMemo(() => {
    const sortParams = searchParams.getAll('sort')
    return sortParams.length > 0
      ? sortParams
      : AI_CREDIT_USAGE_HISTORY_DEFAULT_SORT_COLUMN
  }, [searchParams])

  // AIクレジット使用履歴のデータ
  const { data: getAiCreditUsageHistoryResult } =
    useGetAiCreditUsageHistoriesQuery({
      usageDateFrom: getParamAsString('usageDateFrom', params),
      usageDateTo: getParamAsString('usageDateTo', params),
      userId: getParamAsString('userId', params),
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      sort: sortParams,
    })

  const aiCreditUsageHistories =
    getAiCreditUsageHistoryResult?.aiCreditUsageHistories ?? []

  const totalCount = getAiCreditUsageHistoryResult?.totalCount

  // ソーティングの状態。クエリパラメータから初期値を取得
  // クエリパラメータがない場合は、利用日時で降順にソート
  const sorting: SortingState = useMemo(() => {
    const sortArray =
      getParamAsArray('sort', params) ||
      AI_CREDIT_USAGE_HISTORY_DEFAULT_SORT_COLUMN
    return sortArray.map((sort) => {
      const [id, direction] = sort.split('.')
      return {
        id,
        desc: direction === 'desc',
      }
    })
  }, [params])

  // APIの戻りの総件数を元に、ページ数を計算
  const pageCount = Math.ceil((totalCount ?? 0) / pagination.pageSize)

  // ソート変更時のイベントハンドラ
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      let values: SortingState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(sorting)
      } else {
        values = updaterOrValue
      }

      const sortList = values.map((sort) => {
        return `${sort.id}.${sort.desc ? 'desc' : 'asc'}`
      })

      const newParams = {
        ...params,
        sort: sortList,
        pageIndex: String(0),
      }

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })

      // 文字列化したものを比較し、同じであればnavigateしない
      if (_.isEqual(searchParams.toString(), newQueryString)) return

      navigate(`?${newQueryString}`)
    },
    [navigate, params, searchParams, sorting]
  )

  // ページネーション変更時のイベントハンドラ
  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      let values: PaginationState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(pagination)
      } else {
        values = updaterOrValue
      }

      const nextPageIndex =
        values.pageSize !== pagination.pageSize ? 0 : values.pageIndex

      const newParams = {
        ...params,
        pageIndex: String(nextPageIndex),
        pageSize: String(values.pageSize),
      }

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })

      // 文字列化したものを比較し、同じであればnavigateしない
      if (_.isEqual(searchParams.toString(), newQueryString)) return
      navigate(`?${newQueryString}`)
    },
    [navigate, pagination, params, searchParams]
  )

  // 「使用対象」列（アイコン + ラベル）のrender
  const renderTargetContent = useCallback(
    (entityType: EntityType, label: string) => {
      return (
        <div className="flex flex-row items-center gap-2">
          <CrewEntityIcon
            entityType={entityType}
            width={24}
            height={24}
            className="crew-text-default"
          />
          <span className="line-clamp-2 break-all" title={label}>
            {label}
          </span>
        </div>
      )
    },
    []
  )

  // タスクのリンクをレンダリングする関数
  // アイコン + タスク名
  const renderTaskLink = useCallback(
    (row: AiCreditUsageHistoryTableColumns) => {
      // タスクが削除済みかどうか
      const isDeletedTask = row.target.entityName === null

      // タスク名
      const taskName = row.target.entityName

      // プロジェクト名
      const projectName =
        row.target.parentProject?.projectType === ProjectType.Schedule
          ? t('label.personalSchedule')
          : (row.target.parentProject?.projectName ?? '')

      return isDeletedTask ? (
        // タスクが削除済みの場合：(削除済み)と表示
        renderTargetContent(EntityType.Task, t('label.deleted'))
      ) : (
        // タスクが削除されていない場合：[プロジェクト名/タスク名]のリンクを表示
        <CrewLink
          className="text-md"
          to={`/${row.target.entityType}/${
            row.target.entityRecordId
          }/${getDefaultTabValue(TaskDetailListTabs)}`}
        >
          {renderTargetContent(EntityType.Task, `${projectName}/${taskName}`)}
        </CrewLink>
      )
    },
    [renderTargetContent, t]
  )

  // 会議のリンクをレンダリングする関数
  // アイコン + 会議名
  const renderEventLink = useCallback(
    (row: AiCreditUsageHistoryTableColumns) => {
      // 会議が削除済みかどうか
      const isDeletedEvent = row.target.entityName === null

      // 会議名
      const eventName = row.target.entityName

      // プロジェクト名
      const projectName =
        row.target.parentProject?.projectType === ProjectType.Schedule
          ? t('label.personalSchedule')
          : (row.target.parentProject?.projectName ?? '')

      // 削除済みはリンクなし、未削除は詳細画面へのリンクあり
      return isDeletedEvent ? (
        // 会議が削除済みの場合：(削除済み)と表示
        renderTargetContent(EntityType.Event, t('label.deleted'))
      ) : (
        // 会議が削除されていない場合：[プロジェクト名/会議名]のリンクを表示
        <CrewLink
          className="text-md"
          to={`/${row.target.entityType}/${
            row.target.entityRecordId
          }/${getDefaultTabValue(EventDetailTabs)}`}
        >
          {renderTargetContent(EntityType.Event, `${projectName}/${eventName}`)}
        </CrewLink>
      )
    },
    [renderTargetContent, t]
  )

  // 使用対象の内容をレンダリングする関数
  const usageTargetRender = useCallback(
    (row: AiCreditUsageHistoryTableColumns) => {
      switch (row.category) {
        case AiCreditUsageTargetDomain.AiAssistant:
          // 分類がアシスタントの場合は、使用対象にはそのユーザーを表示する
          return (
            <CrewUserAvatar
              userId={row.usedBy.id}
              displayName={row.usedBy.displayName}
              size={CrewAvatarSize.xs}
              cacheValue={row.usedBy.id + row.usedBy.version}
              showLabel={true}
              labelColor="crew-link"
            />
          )
        case AiCreditUsageTargetDomain.MeetingMinutesGeneration:
          // 分類が議事録の場合は、使用対象には会議を表示する
          return renderEventLink(row) /* 会議の場合はイベントリンクを表示 */
        case AiCreditUsageTargetDomain.RelatedSuggestion:
          // 分類が関連情報の場合は、会議もしくはタスクを表示する
          if (row.target.entityType === EntityType.Event) {
            // 会議の場合
            return renderEventLink(row)
          }
          if (row.target.entityType === EntityType.Task) {
            // タスクの場合
            return renderTaskLink(row)
          }

          return null
        default:
          return null
      }
    },
    [renderEventLink, renderTaskLink]
  )

  // テーブルの列定義
  const columns = useMemo<ColumnDef<AiCreditUsageHistoryTableColumns>[]>(
    () => [
      {
        // 分類
        id: 'category',
        accessorKey: 'category',
        header: () => t('label.category'),
        cell: ({ row }) => {
          const category = getEnumDataByValue(
            AiCreditUsageTarget,
            row.original.category
          )

          const categoryName = category ? t(`${category.text}`) : ''

          return <div className="w-full truncate">{categoryName}</div>
        },
        size: 80,
        minSize: 50,
      },
      {
        // 使用対象
        id: 'target',
        accessorKey: 'target',
        header: () => t('label.usageTarget'),
        cell: ({ row }) => {
          return <>{usageTargetRender(row.original)}</>
        },
        size: 120,
        minSize: 50,
      },
      {
        // 使用数
        id: 'count',
        accessorKey: 'count',
        header: () => t('label.usageCount'),
        cell: ({ row }) => (
          <div className="text-right w-full truncate">{row.original.count}</div>
        ),
        size: 40,
        minSize: 30,
      },
      {
        // 利用者
        id: 'usedBy',
        accessorKey: 'usedBy',
        header: () => t('label.usedBy'),
        cell: ({ row }) => (
          <CrewUserAvatar
            userId={row.original.usedBy.id}
            displayName={row.original.usedBy.displayName}
            size={CrewAvatarSize.xs}
            cacheValue={row.original.usedBy.id + row.original.usedBy.version}
            showLabel={true}
            labelColor="crew-link"
          />
        ),
        size: 80,
        minSize: 50,
      },
      {
        // 利用日時
        id: 'usedAt',
        accessorKey: 'usedAt',
        header: () => t('label.usedAt'),
        cell: ({ row }) => (
          <div className="w-full truncate">
            {dayjs(row.original.usedAt).format(JsonDateFormat.YYYYMMDDHHmmss)}
          </div>
        ),
        size: 80,
        minSize: 50,
      },
    ],
    [t, usageTargetRender]
  )

  // テーブルのオプション
  const tableOptions: TableOptions<AiCreditUsageHistoryTableColumns> = {
    data: aiCreditUsageHistories,
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    pageCount,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableMultiSort: true,
    maxMultiSortColCount: 2,
    meta: {
      headerRowHeight: 35,
      dataRowHeight: 40,
    },
  }

  return (
    <div className="flex-1 overflow-y-hidden">
      <CrewTable tableOptions={tableOptions} />
    </div>
  )
})
